<template>
  <div>
    <div align="center" v-if="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        class="ma-8"
      ></v-progress-circular>
    </div>
    <v-card flat :style="`height: calc(100vh - 25px - 55px);overflow-y: auto;`" v-if="voicemail" align="center">
      <v-toolbar flat class="px-2" data-testid="voicemail-detail-toolbar">
        <v-btn icon class="mr-4" @click="close" data-testid="voicemail-detail-back-btn"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <v-spacer />
        <div v-if="isOnMobile">
          <actions
            :voicemail="voicemail"
            @xfer-msg="$emit('xfer-msg', $event)"
            @del-msg="$emit('del-msg', $event)"
            @move-to-custom-folder="$emit('move-to-custom-folder', $event)"
            @remove-from-custom-folder="$emit('remove-from-custom-folder', $event)" />
        </div>
        <div v-else>
          <div v-if="voicemailDisplayIndex > 0">
            <span class="text-caption font-weight-light">{{ $t('vmail.detail.pages', [voicemailDisplayIndex, voicemailsLength]) }}</span>
            <v-btn icon class="ml-2" :disabled="voicemailDisplayIndex === 1" @click="previousPage()" data-testid="voicemail-detail-previous-btn">
              <v-icon>mdi-menu-left</v-icon>
            </v-btn>
            <v-btn icon @click="nextPage()" :disabled="voicemailDisplayIndex === voicemailsLength" data-testid="voicemail-detail-next-btn">
              <v-icon>mdi-menu-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-toolbar>
      <div class="px-4" style="max-width: 1100px;">
        <msg-header
          :voicemail="voicemail"
          @xfer-msg="$emit('xfer-msg', $event)"
          @del-msg="$emit('del-msg', $event)"
          @move-to-custom-folder="$emit('move-to-custom-folder', $event)"
          @remove-from-custom-folder="$emit('remove-from-custom-folder', $event)"
        />
        <div class="d-flex justify-space-between mt-6" v-if="displaySummaryHeader">
          <span class="text-caption">{{ $t('vmail.hdrs.summary') }}</span>
          <v-chip v-if="displayCategory" small :style="{ 'pointer-events': 'none' }" data-testid="voicemail-detail-category-chip">
            {{ voicemail.transcriptionCategory }}
          </v-chip>
        </div>
        <div class="d-flex text-h6 font-weight-regular" align="left" v-if="displaySummary">
          <span>{{ voicemail.transcriptionSummary }}</span>
        </div>
        <v-spacer />
        <v-card outlined class="pa-6 mt-6">
          <audio-player :src="playerSrc" v-if="playerSrc" class="mr-1" />
          <div align="left">
            <v-btn-toggle v-if="displayOrigTranscriptionToggle" v-model="displayOrigTranscription" class="mt-8 mb-2" data-testid="voicemail-detail-toggle">
              <v-btn small data-testid="voicemail-detail-toggle-translation">
                <v-icon small>mdi-translate</v-icon>
              </v-btn>
              <v-btn small data-testid="voicemail-detail-toggle-original">
                <v-icon small>mdi-text-box</v-icon>
              </v-btn>
            </v-btn-toggle>
          </div>
          <div
            v-if="displayTranscription && displayOrigTranscription === 0" data-testid="voicemail-detail-transcription"
            :class="displayOrigTranscriptionToggle ? 'd-flex font-weight-light' : 'd-flex font-weight-light mt-6'"
            align="left"
          >
            {{ transcription }}
          </div>
          <div v-if="displayOrigTranscriptionText" class="d-flex font-weight-light" align="left" data-testid="voicemail-detail-original-transcription">
            {{ origTranscription }}
          </div>
        </v-card>
      </div>
    </v-card>
    <v-card flat :style="`height: calc(100vh - 25px - 55px);overflow-y: auto;`" v-else align="center">
      <v-alert
        dense
        outlined
        class="ma-8"
        type="error"
      >
      {{ $t('vmail.detail.errors.noVoicemailFound') }}
      </v-alert>
      <v-btn @click="goBack">
        {{ $t('vmail.btn.goBack') }}
      </v-btn>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AudioPlayer from '@/components/audio_player'
import MsgHeader from '@/components/voicemails/msg_detail/msg_header/index'
import Actions from '@/components/voicemails/msg_detail/msg_header/actions'
import API from '@/api'
import events from 'Store/events'

export default {
  name: 'voicemails-msg-detail',
  data: () => ({
    playerSrc: null,
    displayOrigTranscription: 0,
    loading: false
  }),
  components: { AudioPlayer, MsgHeader, Actions },
  computed: {
    ...mapGetters({
      vmbox: 'voicemails/vmboxSettings',
      voicemails: 'voicemails/messages',
      voicemailFilter: 'voicemails/filter',
      voicemailByID: 'voicemails/messageDetail' // API fallback to messageDetail if voicemails/messages is empty
    }),
    currentFolder () {
      return (this.voicemailFilter?.folder === 'all' ? null : this.voicemailFilter?.folder) || null
    },
    transcription () {
      if (this.translationEnabled) {
        return this.voicemail?.translation || this.voicemail?.transcription
      } else {
        return this.voicemail?.transcription
      }
    },
    origTranscription () {
      return this.voicemail?.transcription
    },
    translation () {
      return this.voicemail?.translation
    },
    summary () {
      return this.voicemail?.transcriptionSummary || ''
    },
    voicemailId () {
      return parseInt(this.$route.params.id)
    },
    voicemailIndex () {
      return this.voicemails?.findIndex((vm) => vm.id === this.voicemailId)
    },
    voicemailsLength () {
      return this.voicemails?.length || 0
    },
    voicemailDisplayIndex () {
      return (this.voicemailIndex + 1) || 0
    },
    voicemail () {
      return this.voicemails?.find((vm) => vm.id === this.voicemailId) || this.voicemailByID
    },
    transcriptionEnabled () {
      return this.vmbox?.transcript
    },
    summaryEnabled () {
      return this.vmbox?.summarize
    },
    categoryEnabled () {
      return this.vmbox?.categorize
    },
    translationEnabled () {
      return this.vmbox?.translate
    },
    displayTranscription () {
      return this.transcriptionEnabled && this.transcription && this.transcription !== ''
    },
    displaySummary () {
      return this.summaryEnabled && this.voicemail.transcriptionSummary
    },
    displayCategory () {
      return this.categoryEnabled && this.voicemail.transcriptionCategory
    },
    displaySummaryHeader () {
      return this.displaySummary || this.displayCategory
    },
    displayOrigTranscriptionToggle () {
      return this.displayTranscription && this.translationEnabled && this.translation
    },
    displayOrigTranscriptionText () {
      return this.displayTranscription && this.translation && this.displayOrigTranscription === 1
    },
    isDark () {
      return this.$vuetify.theme.dark
    },
    isOnMobile () {
      return this.$vuetify.breakpoint.mobile
    }
  },
  watch: {
    voicemail: {
      handler (newVoicemail, oldVoicemail) {
        if (this.voicemailId && newVoicemail && newVoicemail?.id !== oldVoicemail?.id) {
          this.play(newVoicemail)
        }
      },
      deep: true,
      immediate: true
    },
    voicemailId: {
      handler (newId, oldId) {
        if (newId && newId !== oldId) {
          const vmInList = this.voicemails?.some((vm) => vm.id === this.voicemailId)
          if (!vmInList) {
            this.loading = true
            this.getVoicemailById(newId).catch(() => {
              alert('Voicemail not found. Please try again later.')
            }).finally(() => {
              this.loading = false
            })
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      setAsRead: 'voicemails/setAsRead',
      getVoicemailById: 'voicemails/getVoicemailById'
    }),
    close () {
      this.$router.push({ name: 'voicemails', params: { folder: this.currentFolder } })
    },
    async play (voicemail) {
      this.playerSrc = null
      try {
        const response = await API.VMAudioSrc(voicemail.id)
        this.playerSrc = URL.createObjectURL(response.data)
      } catch {
        events.pubError(`Failed to load voicemail audio for message id ${voicemail.id}`)
      }
      this.setAsRead(voicemail.id)
    },
    nextPage () {
      const voicemailId = this.voicemails[this.voicemailIndex + 1]?.id
      if (voicemailId && this.voicemailIndex + 1 < this.voicemailsLength) {
        this.$router.push({ name: 'voicemails', params: { folder: 'detail', id: voicemailId } })
      }
    },
    previousPage () {
      const voicemailId = this.voicemails[this.voicemailIndex - 1]?.id
      if (voicemailId && this.voicemailIndex > 0) {
        this.$router.push({ name: 'voicemails', params: { folder: 'detail', id: voicemailId } })
      }
    },
    goBack () {
      if (this.$route.params.folder !== undefined && this.$route.params.folder !== '') {
        this.$router.push({ name: 'voicemails' })
      }
    }
  }
}
</script>
<style scoped>
.no-hover-effect:hover {
  background-color: inherit !important;
  box-shadow: none !important;
}
</style>
